/**
 * Routing and the auth boundary.
 *
 * Everything except `/login` sits behind `RequireAuth`. Route-level capability
 * checks sit on top, so a support account landing on `/settings` gets a clear
 * "not permitted" page rather than a broken screen.
 */

import { useEffect, lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';
import AdminLayout from './components/AdminLayout';
import ChangePasswordPage from './routes/ChangePasswordPage';
import LoginPage from './routes/LoginPage';
import { useAuth, useCurrentUser } from './store/auth';
import { can, landingPath, type Capability } from './lib/accounts';
import { Button, EmptyState } from './components/ui';
import { useDb } from './store/db';

/* Route-level code splitting: the login screen and shell load first, and each
   section arrives when it is first opened. */
const DashboardPage = lazy(() => import('./routes/DashboardPage'));
const BookingsPage = lazy(() => import('./routes/BookingsPage'));
const PackagesPage = lazy(() => import('./routes/ServicesPage'));
const ProductionPage = lazy(() => import('./routes/ProductionPage'));
const CategoriesPage = lazy(() => import('./routes/CategoriesPage'));
const TherapistsPage = lazy(() => import('./routes/TherapistsPage'));
const ApplicationsPage = lazy(() => import('./routes/ApplicationsPage'));
const PayoutsPage = lazy(() => import('./routes/PayoutsPage'));
const CustomersPage = lazy(() => import('./routes/CustomersPage'));
const OffersPage = lazy(() => import('./routes/OffersPage'));
const ReviewsPage = lazy(() => import('./routes/ReviewsPage'));
const NotificationsPage = lazy(() => import('./routes/NotificationsPage'));
const BannersPage = lazy(() => import('./routes/BannersPage'));
const LocationsPage = lazy(() => import('./routes/LocationsPage'));
const SettingsPage = lazy(() => import('./routes/SettingsPage'));
const EmployeesPage = lazy(() => import('./routes/EmployeesPage'));

function RequireAuth({ children }: { children: ReactNode }) {
  const session = useAuth((s) => s.session);
  const status = useAuth((s) => s.status);
  const bootstrap = useAuth((s) => s.bootstrap);
  const location = useLocation();

  useEffect(() => {
    if (status === 'checking') void bootstrap();
  }, [bootstrap, status]);

  if (status === 'checking') return <RouteFallback />;
  if (!session) {
    // Remember where they were headed so sign-in can return them there.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  if (session.mustChangePassword) return <ChangePasswordPage />;
  return <>{children}</>;
}

function RequireData({ children }: { children: ReactNode }) {
  const hydrated = useDb((state) => state.hydrated);
  const loading = useDb((state) => state.loading);
  const error = useDb((state) => state.error);
  const hydrate = useDb((state) => state.hydrate);

  useEffect(() => {
    if (!hydrated && !loading) void hydrate();
  }, [hydrate, hydrated, loading]);

  if (!hydrated && (loading || !error)) return <RouteFallback />;
  if (!hydrated && error) {
    return (
      <div className="card m-6">
        <EmptyState
          icon={<ShieldOff size={20} strokeWidth={2} aria-hidden />}
          title="Could not load operations data"
          body={error}
          action={<Button size="md" onClick={() => void hydrate(true)} label="Try again" />}
        />
      </div>
    );
  }
  return <>{children}</>;
}

function RequireCapability({
  capability,
  children,
}: {
  capability: Capability;
  children: ReactNode;
}) {
  const user = useCurrentUser();

  if (!can(user, capability)) {
    return (
      <div className="card">
        <EmptyState
          icon={<ShieldOff size={20} strokeWidth={2} aria-hidden />}
          title="Not permitted"
          body="Your account does not have access to this page. Ask an administrator if you need it."
          action={<Button variant="secondary" size="md" onClick={() => history.back()} label="Go back" />}
        />
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * The console's front page: the dashboard for an account that has it, otherwise the
 * first page the account may open.
 */
function Home() {
  const user = useCurrentUser();
  if (can(user, 'dashboard.view')) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <DashboardPage />
      </Suspense>
    );
  }
  const landing = landingPath(user);
  if (landing && landing !== '/') return <Navigate to={landing} replace />;
  return (
    <div className="card">
      <EmptyState
        icon={<ShieldOff size={20} strokeWidth={2} aria-hidden />}
        title="No pages yet"
        body="Your login works, but no console pages have been given to it. Ask an administrator to choose your pages on the Employees page."
      />
    </div>
  );
}

function LegacyServicesRedirect() {
  const location = useLocation();
  return <Navigate to={`/packages${location.search}`} replace />;
}

/** The Kits page became Production; old links and bookmarks keep their filters. */
function LegacyKitsRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  // The old default tab was the overview, which is no longer the default.
  if (!params.get('tab')) params.set('tab', 'overview');
  return <Navigate to={`/production?${params.toString()}`} replace />;
}

function RouteFallback() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label="Loading section">
      <div className="skeleton h-9 w-64" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="skeleton h-28" />
        ))}
      </div>
      <div className="skeleton h-72" />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route
          element={
            <RequireAuth>
              <RequireData>
                <AdminLayout />
              </RequireData>
            </RequireAuth>
          }
        >
          <Route index element={<Home />} />

          <Route
            path="bookings"
            element={
              <RequireCapability capability="bookings.view">
                <Suspense fallback={<RouteFallback />}>
                  <BookingsPage />
                </Suspense>
              </RequireCapability>
            }
          />

          <Route
            path="packages"
            element={
              <RequireCapability capability="catalogue.view">
                <Suspense fallback={<RouteFallback />}>
                  <PackagesPage />
                </Suspense>
              </RequireCapability>
            }
          />

          <Route path="services" element={<LegacyServicesRedirect />} />

          <Route
            path="production"
            element={
              <RequireCapability capability="production.view">
                <Suspense fallback={<RouteFallback />}>
                  <ProductionPage />
                </Suspense>
              </RequireCapability>
            }
          />

          <Route path="kits" element={<LegacyKitsRedirect />} />

          <Route
            path="categories"
            element={
              <RequireCapability capability="categories.view">
                <Suspense fallback={<RouteFallback />}>
                  <CategoriesPage />
                </Suspense>
              </RequireCapability>
            }
          />

          <Route
            path="therapists"
            element={
              <RequireCapability capability="therapists.view">
                <Suspense fallback={<RouteFallback />}>
                  <TherapistsPage />
                </Suspense>
              </RequireCapability>
            }
          />

          <Route
            path="applications"
            element={
              <RequireCapability capability="applications.view">
                <Suspense fallback={<RouteFallback />}>
                  <ApplicationsPage />
                </Suspense>
              </RequireCapability>
            }
          />

          <Route
            path="payouts"
            element={
              <RequireCapability capability="payouts.view">
                <Suspense fallback={<RouteFallback />}>
                  <PayoutsPage />
                </Suspense>
              </RequireCapability>
            }
          />

          <Route
            path="customers"
            element={
              <RequireCapability capability="customers.view">
                <Suspense fallback={<RouteFallback />}>
                  <CustomersPage />
                </Suspense>
              </RequireCapability>
            }
          />

          <Route
            path="offers"
            element={
              <RequireCapability capability="offers.view">
                <Suspense fallback={<RouteFallback />}>
                  <OffersPage />
                </Suspense>
              </RequireCapability>
            }
          />

          <Route
            path="reviews"
            element={
              <RequireCapability capability="reviews.view">
                <Suspense fallback={<RouteFallback />}>
                  <ReviewsPage />
                </Suspense>
              </RequireCapability>
            }
          />

          <Route
            path="notifications"
            element={
              <RequireCapability capability="notifications.view">
                <Suspense fallback={<RouteFallback />}>
                  <NotificationsPage />
                </Suspense>
              </RequireCapability>
            }
          />

          <Route
            path="banners"
            element={
              <RequireCapability capability="banners.view">
                <Suspense fallback={<RouteFallback />}>
                  <BannersPage />
                </Suspense>
              </RequireCapability>
            }
          />

          <Route
            path="locations"
            element={
              <RequireCapability capability="locations.view">
                <Suspense fallback={<RouteFallback />}>
                  <LocationsPage />
                </Suspense>
              </RequireCapability>
            }
          />

          <Route
            path="settings"
            element={
              <RequireCapability capability="settings.view">
                <Suspense fallback={<RouteFallback />}>
                  <SettingsPage />
                </Suspense>
              </RequireCapability>
            }
          />

          <Route
            path="employees"
            element={
              <RequireCapability capability="staff.manage">
                <Suspense fallback={<RouteFallback />}>
                  <EmployeesPage />
                </Suspense>
              </RequireCapability>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
